import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {NGXLogger} from 'ngx-logger';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FakturierungsbelegFormActions} from '../actions/fakturierungsbeleg-form.actions';
import {concatMap, EMPTY, Observable, of, switchMap, take, tap, withLatestFrom} from 'rxjs';
import {catchError, expand, filter, last, map} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {SentryActions} from '../actions/sentry.actions';
import {FakturierungsbelegFormSelectors} from '../selectors/fakturierungsbeleg-form.selectors';
import {AppState} from '../states/app.state';
import {Store} from '@ngrx/store';
import {
  CreatePositionRequestDTO,
  EinstellungenService,
  NachlaufzeileService,
  PositionDTO,
  PositionService,
  ProblemDTO,
  ProduktDTO,
} from '../../openapi/fakturierung-openapi';
import {InhaberEntitiesSelectors, mappedHttpErrorResponseOperator} from '@adnova/jf-ng-components';
import {Router} from '@angular/router';
import {FakturierungsbelegEntitiesActions} from '../actions/fakturierungsbeleg-entities.actions';
import {UploadLogoDialogActions} from '../actions/upload-logo-dialog.actions';
import {FakturierungsbelegEntitiesSelectors} from '../selectors/fakturierungsbeleg-entities.selectors';


@Injectable()
export class FakturierungsbelegFormEffects {

  constructor(
    private actions$: Actions,
    private logger: NGXLogger,
    private snackbar: MatSnackBar,
    private httpClient: HttpClient,
    private router: Router,
    private nachlaufzeileService: NachlaufzeileService,
    private store: Store<AppState>,
    private positionService: PositionService,
    private einstellungenService: EinstellungenService,
  ) {
  }


  readonly handleGetFakturierungsbelegByIdSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.getFakturierungsbelegByIdSuccess),
      withLatestFrom(this.store.select(FakturierungsbelegFormSelectors.belegId)),
      // INFO: Verknuepfte Lieferscheine des geoeffneten Belegs werden ueber dieselbe
      //       getFakturierungsbelegById-Action geladen. Ohne den Filter wuerde deren
      //       Success den geoeffneten Beleg im Formular ueberschreiben — der Editor
      //       zeigte dann Belegart, Labels und Feld-Gating des Lieferscheins.
      filter(([{belegDto}, formBelegId]) => !formBelegId || belegDto.id === formBelegId),
      map(([{belegDto}]) =>
        FakturierungsbelegFormActions.updateFakturierungsbelegDto({fakturierungsbelegDto: belegDto})
      ),
    )
  );

  // INFO: Nur einmal je Beleg — getFakturierungsbelegByIdSuccess feuert auch nach jedem Update.
  readonly hydrateReferenzfelder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.getFakturierungsbelegByIdSuccess),
      withLatestFrom(
        this.store.select(FakturierungsbelegFormSelectors.belegId),
        this.store.select(FakturierungsbelegFormSelectors.referenzfelderHydratedBelegId),
      ),
      filter(([{belegDto}, formBelegId, hydratedBelegId]) =>
        (!formBelegId || belegDto.id === formBelegId) && belegDto.id !== hydratedBelegId
      ),
      map(([{belegDto}]) => FakturierungsbelegFormActions.hydrateReferenzfelder({belegDto})),
    )
  );

  readonly addPosition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.addPosition),
      withLatestFrom(
        this.store.select(InhaberEntitiesSelectors.currentInhaberId),
        this.store.select(FakturierungsbelegFormSelectors.belegId)
      ),
      concatMap(([{produktDto}, betriebId, belegId]) => {
        if (!betriebId || !belegId) {
          this.logger.error('betriebId or belegId is missing!');
          return of(FakturierungsbelegFormActions.addPositionFailure({error: {message: 'missing ids'}}));
        }

        const createPositionRequestDTO: CreatePositionRequestDTO = this.buildCreatePositionRequestDTO(produktDto);

        return this.positionService.createPosition(betriebId, belegId, createPositionRequestDTO).pipe(
          concatMap(positionDto => {
            this.logger.debug('newly added position', positionDto);

            return [
              FakturierungsbelegFormActions.addPositionSuccess({newlyCreatedPosition: positionDto}),
              FakturierungsbelegEntitiesActions.getFakturierungsbelegById({betriebId, belegId})
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map(error => {
              this.logger.error('add position failed.', 'error:', error);

              return FakturierungsbelegFormActions.addPositionFailure({error});
            }),
          )),
        );
      })
    )
  );

  readonly addPositionFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.addPositionFailure),
      map(action => {
        let errorMsg = '';
        switch (action.error.status) {
          case 400: {
            errorMsg = action.error.error?.detail || 'Ungültige Anfrage. Position konnte nicht erstellt werden.';
            break;
          }
          case 403: {
            errorMsg = 'Fehlende Berechtigung zum Erstellen einer Position. Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404: {
            errorMsg = 'Beleg nicht gefunden. Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice';
            break;
          }
          default: {
            errorMsg = 'Ein unbekannter Fehler ist aufgetreten. Bitte versuche es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );

        return SentryActions.captureException({
          error: action.error,
          extras: {
            errorMsg,
          },
        });
      })
    )
  );

  readonly fetchPositionen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.fetchPositionen),
      withLatestFrom(this.store.select(InhaberEntitiesSelectors.currentInhaberId)),
      concatMap(([{belegId}, betriebId]) => {
        if (!betriebId || !belegId) {
          this.logger.error('betriebId or belegId is missing!');
          return of(FakturierungsbelegFormActions.fetchPositionenFailure({error: {message: 'missing ids'}}));
        }

        const pageSize = 10;
        let allPositionen: PositionDTO[] = [];

        return this.positionService.readPositionen(betriebId, belegId, {number: 0, size: pageSize}).pipe(
          expand(page => {
            allPositionen = [...allPositionen, ...page.content];
            const currentPage = page.pageable?.number ?? 0;
            const totalElements = page.totalElements ?? 0;
            const hasMorePages = (currentPage + 1) * pageSize < totalElements;

            return hasMorePages
              ? this.positionService.readPositionen(betriebId, belegId, {number: currentPage + 1, size: pageSize})
              : EMPTY;
          }),
          last(),
          map(() => FakturierungsbelegFormActions.fetchPositionenSuccess({positionen: allPositionen})),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => {
              this.logger.error('fetch position failed.', 'error:', error);

              return FakturierungsbelegFormActions.fetchPositionenFailure({error});
            }),
          )),
        );
      })
    )
  );

  readonly fetchPositionenFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.fetchPositionenFailure),
      map(action => {
        let errorMsg = '';
        switch (action.error.status) {
          case 403: {
            errorMsg = 'Fehlende Berechtigung zum Anzeigen der Positionen. Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          case 404: {
            errorMsg = 'Beleg nicht gefunden. Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Laden der Positionen. Bitte versuche es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );

        return SentryActions.captureException({
          error: action.error,
          extras: {
            errorMsg,
          },
        });
      })
    )
  );

  readonly updatePosition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.updatePosition),
      withLatestFrom(
        this.store.select(InhaberEntitiesSelectors.currentInhaberId),
        this.store.select(FakturierungsbelegFormSelectors.belegId),
      ),
      concatMap(([{updatePositionObj}, betriebId, belegId]) => {
        const {positionId, payload} = updatePositionObj;

        if (!betriebId || !belegId) {
          this.logger.error('betriebid or belegid is missing!');
          return of(
            SentryActions.captureException({
              error: {message: 'updatePosition: betriebId or belegId is missing'},
              extras: {positionId, betriebId, belegId},
            }),
            FakturierungsbelegFormActions.updatePositionFailure({
              positionId,
              error: {message: 'missing ids'},
            }),
          );
        }

        return this.positionService.updatePosition(betriebId, belegId, positionId, payload).pipe(
          concatMap(positionDto => {
            this.logger.debug('position updated', positionDto);

            return [
              FakturierungsbelegFormActions.updatePositionSuccess({newlyUpdatedPosition: positionDto}),
              FakturierungsbelegEntitiesActions.getFakturierungsbelegById({betriebId, belegId})
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map(error => {
              this.logger.error('update position failed.', 'error:', error);

              return FakturierungsbelegFormActions.updatePositionFailure({positionId, error});
            }),
          )),
        );
      })
    )
  );

  readonly updatePositionFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.updatePositionFailure),
      map(action => {
        let errorMsg = '';

        switch (action.error.status) {
          case 400: {
            errorMsg = action.error.error?.detail || 'Ungültige Anfrage. Position konnte nicht aktualisiert werden.';
            break;
          }
          case 403: {
            errorMsg = 'Der Benutzer besitzt nicht das nötige Recht, um diese Position zu aktualisieren. Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice';
            break;
          }
          case 404: {
            errorMsg = 'Position wurde nicht gefunden. Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice';
            break;
          }
          case 422: {
            errorMsg = 'Aktualisieren der Position nicht möglich. Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          }
          default: {
            errorMsg = 'Ein unbekannter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.';
          }
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );

        return SentryActions.captureException({
          error: action.error,
          extras: {
            errorMsg,
          },
        });
      })
    )
  );

  readonly deletePosition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.deletePosition),
      withLatestFrom(
        this.store.select(InhaberEntitiesSelectors.currentInhaberId),
        this.store.select(FakturierungsbelegFormSelectors.belegId)
      ),
      concatMap(([{positionId}, betriebId, belegId]) => {

        if (!betriebId || !belegId) {
          this.logger.error('betriebId or belegId is missing');
          return of(FakturierungsbelegFormActions.deletePositionFailure({error: {message: 'missing ids'}}));
        }

        return this.positionService.deletePosition(betriebId, belegId, positionId).pipe(
          concatMap(() => {
            this.logger.debug('position deleted', positionId);

            return [
              FakturierungsbelegFormActions.deletePositionSuccess({positionId}),
              FakturierungsbelegEntitiesActions.getFakturierungsbelegById({betriebId, belegId}),
              FakturierungsbelegFormActions.fetchPositionen({belegId}),
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => {
              this.logger.error('delete position failed.', 'error:', error);

              return FakturierungsbelegFormActions.deletePositionFailure({error});
            }),
          )),
        );
      })
    )
  );

  readonly deletePositionFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.deletePositionFailure),
      map(({error}) => {
        let errorMsg = '';

        switch (error.status) {
          case 403:
            errorMsg = 'Der Benutzer besitzt nicht das nötige Recht, um diese Position zu löschen. Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          case 404:
            errorMsg = 'Position wurde nicht gefunden. Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          case 422:
            errorMsg = 'Löschen der Position nicht möglich. Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
            break;
          default:
            errorMsg = 'Ein unbekannter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.';
        }

        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );

        return SentryActions.captureException({
          error,
          extras: {errorMsg},
        });
      })
    )
  );

  readonly uploadLogo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.uploadLogo),
      withLatestFrom(
        this.store.select(FakturierungsbelegFormSelectors.belegId)
      ),
      switchMap(([{objectUrl}, belegId]) =>
        this.store.select(FakturierungsbelegEntitiesSelectors.belegById(belegId!)).pipe(
          take(1),
          map(beleg => ({beleg, objectUrl}))
        )
      ),
      switchMap(({beleg, objectUrl}) =>

        this.httpClient.get(objectUrl, {responseType: 'blob'}).pipe(
          switchMap(blob =>
            this.einstellungenService.uploadLogo(beleg!.betriebId, blob).pipe(
              switchMap(response => {
                this.logger.debug('uploaded logo successfully');
                return [
                  FakturierungsbelegFormActions.uploadLogoSuccess(),
                  UploadLogoDialogActions.close(),
                ];
              }),
              catchError(error =>
                of(error).pipe(
                  mappedHttpErrorResponseOperator(error),
                  map(error => {
                    this.logger.debug('upload logo failed', error);
                    return FakturierungsbelegFormActions.uploadLogoFailure({error});
                  })
                )
              )
            )
          )
        )
      )
    )
  );

  readonly uploadLogoSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.uploadLogoSuccess),
      withLatestFrom(
        this.store.select(FakturierungsbelegFormSelectors.fakturierungsbelegDto)
      ),
      switchMap(([_, fakturierungsbeleg]) => {
        this.snackbar.open(
          'Logo erfolgreich hochgeladen.',
          undefined,
          {
            duration: 5000,
            panelClass: 'success',
          }
        );

        return [
          FakturierungsbelegEntitiesActions.updateFakturierungsbelegEinstellungen({
            betriebId: fakturierungsbeleg!.betriebId!,
            belegId: fakturierungsbeleg!.id!,
          })
        ];
      }),
    ),
  );

  readonly uploadLogoFailure$ = createEffect(() => this.actions$.pipe(
    ofType(FakturierungsbelegFormActions.uploadLogoFailure),
    map(action => {
      let errorMsg = '';
      switch (action.error.status) {
        case 403 : {
          errorMsg = 'Fehlende Berechtigung für das Speichern des Logos.';
          break;
        }
        case 404 : {
          errorMsg = 'Fehler beim Speichern des Logos. Betrieb nicht gefunden. Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice';
          break;
        }
        default: {
          errorMsg = 'Fehler beim Speichern des Logos. Bitte probiere es später erneut.';
        }
      }

      this.snackbar.open(
        errorMsg,
        undefined,
        {
          duration: 5000,
          panelClass: 'error',
        }
      );

      return SentryActions.captureException({
        error: action.error,
        extras: {
          errorMsg,
        },
      });
    })
  ));

  readonly getLogo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.getLogo),
      withLatestFrom(
        this.store.select(InhaberEntitiesSelectors.currentInhaberId),
      ),
      switchMap(([{einstellungRevision}, betriebId]) => {

        if (!betriebId) {
          this.logger.error('betriebId is missing');
          return of(FakturierungsbelegFormActions.getLogoFailure({error: {}}));
        }

        return this.einstellungenService.getLogo(betriebId, einstellungRevision).pipe(
          map(logo => {
            this.logger.debug('loaded logo successfully');
            let objectUrl = '';
            if (logo) {
              // INFO: Nur setzen, wenn ein Logo geladen wurde, kann leer sein, wenn noch kein Logo eingerichtet ist.
              objectUrl = URL.createObjectURL(logo);
            }
            return FakturierungsbelegFormActions.updateLogoUrl({objectUrl});
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => {
              this.logger.debug('get logo failed', error);

              return FakturierungsbelegFormActions.getLogoFailure({error});
            })
          )),
        );
      }),
    )
  );

  readonly getLogoFailure$ = createEffect(() => this.actions$.pipe(
    ofType(FakturierungsbelegFormActions.getLogoFailure),
    map(action => {
      let errorMsg = '';
      switch (action.error.status) {
        case 404: {
          errorMsg = 'Logo nicht gefunden. Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice';
          break;
        }
        default: {
          errorMsg = 'Fehler beim Laden des Logos. Bitte probiere es später erneut.';
        }
      }

      this.snackbar.open(
        errorMsg,
        undefined,
        {
          duration: 5000,
          panelClass: 'error',
        }
      );

      return SentryActions.captureException({
        error: action.error,
        extras: {
          errorMsg,
        },
      });
    })
  ));

  readonly deleteLogo = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.deleteLogo),
      withLatestFrom(
        this.store.select(InhaberEntitiesSelectors.currentInhaberId),
        this.store.select(FakturierungsbelegFormSelectors.belegId),
      ),
      concatMap(([_, betriebId, belegId]) => {
        if (!betriebId) {
          this.logger.error('betriebId is missing');
          return of(FakturierungsbelegFormActions.deleteLogoFailure({error: {}}));
        }
        return this.einstellungenService.deleteLogo(betriebId).pipe(
          concatMap(() => {
            this.logger.debug('deleted logo successfully');
            return [
              FakturierungsbelegFormActions.deleteLogoSuccess(),
              FakturierungsbelegFormActions.updateLogoUrl({objectUrl: undefined}),
              UploadLogoDialogActions.close(),
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => {
              this.logger.debug('delete logo failed', error);
              return FakturierungsbelegFormActions.deleteLogoFailure({error});
            })
          ))
        );
      }),
    )
  );

  readonly deleteLogoSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.deleteLogoSuccess),
      withLatestFrom(
        this.store.select(FakturierungsbelegFormSelectors.fakturierungsbelegDto)
      ),
      switchMap(([_, fakturierungsbeleg]) => {
        this.snackbar.open(
          'Logo erfolgreich entfernt.',
          undefined,
          {
            duration: 5000,
            panelClass: 'success',
          }
        );

        return [
          FakturierungsbelegEntitiesActions.updateFakturierungsbelegEinstellungen({
            betriebId: fakturierungsbeleg!.betriebId!,
            belegId: fakturierungsbeleg!.id!,
          })
        ];
      }),
    ),
  );

  readonly deleteLogoFailure$ = createEffect(() => this.actions$.pipe(
    ofType(FakturierungsbelegFormActions.deleteLogoFailure),
    map(action => {
      let errorMsg = '';
      switch (action.error.status) {
        case 403 : {
          errorMsg = 'Fehlende Berechtigung für das Löschen des Logos.';
          break;
        }
        case 404 : {
          errorMsg = 'Fehler beim Löschen des Logos. Betrieb nicht gefunden. Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice';
          break;
        }
        default: {
          errorMsg = 'Fehler beim Löschen des Logos. Bitte probiere es später erneut.';
        }
      }

      this.snackbar.open(
        errorMsg,
        undefined,
        {
          duration: 5000,
          panelClass: 'error',
        }
      );

      return SentryActions.captureException({
        error: action.error,
        extras: {
          errorMsg,
        },
      });
    })
  ));

  readonly releaseLogoFromMemory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.releaseLogoFromMemory),
      tap(action => {
        URL.revokeObjectURL(action.objectUrl);
      }),
    ), {dispatch: false}
  );

  readonly addNachlaufzeile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.addNachlaufzeile),
      withLatestFrom(this.store.select(InhaberEntitiesSelectors.currentInhaberId)),
      concatMap(([{createNachlaufzeileRequestDTO, belegId}, inhaberId]) =>
        this.nachlaufzeileService.createNachlaufzeile(inhaberId!, belegId, createNachlaufzeileRequestDTO).pipe(
          concatMap(() => {
            return [
              FakturierungsbelegFormActions.addNachlaufzeileSuccess({belegId}),
              FakturierungsbelegEntitiesActions.getFakturierungsbelegById({
                betriebId: inhaberId != null ? inhaberId : '',
                belegId,
              }),
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map(mappedError => {
              this.logger.error('error while adding nachlaufzeile', mappedError, 'for betrieb-id', inhaberId, 'and beleg-id', belegId);
              return FakturierungsbelegFormActions.addNachlaufzeileFailure({error: mappedError});
            })
          ))
        )
      )
    )
  );

  readonly addNachlaufzeileFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.addNachlaufzeileFailure),
      map(action => {
        let errorMsg = '';
        switch (action.error.status) {
          case 400: {
            errorMsg = action.error.error?.detail || 'Ungültige Anfrage. Schlussbemerkung konnte nicht hinzugefügt werden.';
            break;
          }
          case 403: {
            errorMsg = 'Fehlende Berechtigung für das Hinzufügen einer Schlussbemerkung.';
            break;
          }
          case 404: {
            errorMsg = 'Fehler beim Hinzufügen einer Schlussbemerkung. Betrieb nicht gefunden. Bitte probiere es später erneut.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Hinzufügen einer Schlussbemerkung. Bitte probiere es später erneut.';
          }
        }
        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );
        return SentryActions.captureException({
          error: action.error,
          extras: {
            errorMsg,
          },
        });
      })
    )
  );

  readonly removeNachlaufzeile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.removeNachlaufzeile),
      withLatestFrom(this.store.select(InhaberEntitiesSelectors.currentInhaberId)),
      concatMap(([{nachlaufzeileId, belegId}, inhaberId]) =>
        this.nachlaufzeileService.deleteNachlaufzeile(inhaberId != null ? inhaberId : '', belegId, nachlaufzeileId).pipe(
          map(() => FakturierungsbelegFormActions.removeNachlaufzeileSuccess({belegId})),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(mappedError => {
              this.logger.error('error while removing schlussbemerkung', mappedError, 'for betrieb', inhaberId, 'and beleg', belegId);
              return FakturierungsbelegFormActions.removeNachlaufzeileFailure({error: mappedError});
            })
          ))
        )
      )
    )
  );

  readonly removeNachlaufzeileSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.removeNachlaufzeileSuccess),
      withLatestFrom(this.store.select(InhaberEntitiesSelectors.currentInhaberId)),
      map(([{belegId}, inhaberId]) => FakturierungsbelegEntitiesActions.getFakturierungsbelegById({
        betriebId: inhaberId != null ? inhaberId : '',
        belegId
      }))
    )
  );

  readonly removeNachlaufzeileFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.removeNachlaufzeileFailure),
      map(action => {
        let errorMsg = '';
        switch (action.error.status) {
          case 403 : {
            errorMsg = 'Fehlende Berechtigung für das Löschen einer Schlussbemerkung.';
            break;
          }
          case 404 : {
            errorMsg = 'Fehler beim Löschen einer Schlussbemerkung. Inhaber nicht gefunden. Bitte probiere es später erneut.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Löschen einer Schlussbemerkung. Bitte probiere es später erneut.';
          }
        }
        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );
        return SentryActions.captureException({
          error: action.error,
          extras: {
            errorMsg,
          },
        });
      })
    )
  );

  readonly updateNachlaufzeile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.updateNachlaufzeile),
      withLatestFrom(this.store.select(InhaberEntitiesSelectors.currentInhaberId)),
      concatMap(([{nachlaufzeileId, updateNachlaufzeileRequestDTO, belegId}, inhaberId]) =>
        this.nachlaufzeileService.updateNachlaufzeile(inhaberId != null ? inhaberId : '', belegId, nachlaufzeileId, updateNachlaufzeileRequestDTO).pipe(
          map(() => FakturierungsbelegFormActions.updateNachlaufzeileSuccess({belegId})),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(mappedError => {
              this.logger.error('error while updating nachlaufzeile', mappedError, 'for betrieb', inhaberId, 'and beleg', belegId);
              return FakturierungsbelegFormActions.updateNachlaufzeileFailure({error: mappedError});
            })
          ))
        )
      )
    )
  );

  readonly updateNachlaufzeileSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.updateNachlaufzeileSuccess),
      withLatestFrom(this.store.select(InhaberEntitiesSelectors.currentInhaberId)),
      map(([{belegId}, inhaberId]) => FakturierungsbelegEntitiesActions.getFakturierungsbelegById({
        betriebId: inhaberId != null ? inhaberId : '',
        belegId
      }))
    )
  );

  readonly updateNachlaufzeileFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.updateNachlaufzeileFailure),
      map(action => {
        let errorMsg = '';
        switch (action.error.status) {
          case 403 : {
            errorMsg = 'Fehlende Berechtigung für das Aktualisieren einer Schlussbemerkung.';
            break;
          }
          case 404 : {
            errorMsg = 'Fehler beim Aktualisieren einer Schlussbemerkung. Betrieb nicht gefunden. Bitte probiere es später erneut.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Aktualisieren einer Schlussbemerkung. Bitte probiere es später erneut.';
          }
        }
        this.snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );
        return SentryActions.captureException({
          error: action.error,
          extras: {
            errorMsg,
          },
        });
      })
    )
  );


  /**
   * Effekt zum weiterleiten auf die Fakturierungsbeleg-Details-Seite nach dem erstellen eines neuen Initialen Belegs:
   * /fakturierungsbelege/inhaber/<inhaberId>/edit/fakturierungsbeleg/<belegId>
   */
  readonly navigateToNewBeleg$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FakturierungsbelegFormActions.redirectToForm),
      tap(({belegId, betriebId}) => {
        this.router.navigate([`/fakturierungsbelege/inhaber/${betriebId}/edit/fakturierungsbeleg/${belegId}`]);
      })
    ), {dispatch: false},
  );


  // INFO : Placeholder method, until the backend is implemented.
  private saveLogo(inhaberId: string, body: Blob): Observable<boolean> {
    if (inhaberId === null || inhaberId === undefined) {
      throw new Error('Required parameter inhaberId was null or undefined when calling saveLogo.');
    }
    if (body === null || body === undefined) {
      throw new Error('Required parameter body was null or undefined when calling saveLogo.');
    }

    console.log('Temporary solution: Simulating successful save of logo.');
    return of(true);  // Simulate successful response
  }

  // INFO : Placeholder method, until the backend is implemented.
  private updateLogo(inhaberId: string, body: Blob): Observable<boolean> {
    if (inhaberId === null || inhaberId === undefined) {
      throw new Error('Required parameter inhaberId was null or undefined when calling updateLogo.');
    }
    if (body === null || body === undefined) {
      throw new Error('Required parameter body was null or undefined when calling updateLogo.');
    }

    console.log('Temporary solution: Simulating successful update of logo.');
    return of(true);  // Simulate successful response
  }

  // INFO : Placeholder method, until the backend is implemented.
  private getLogo(inhaberId: string): Observable<any> {
    if (!inhaberId) {
      throw new Error('Required parameter inhaberId was null or undefined when calling getLogo.');
    }

    console.log('Temporary solution: Simulating successful logo fetching.');
    return of(null);
  }

  private buildCreatePositionRequestDTO(produktDto: ProduktDTO): CreatePositionRequestDTO {
    let einzelpreisNetto = 0;
    if (produktDto.berechnungsregel && 'betrag' in produktDto.berechnungsregel && typeof produktDto.berechnungsregel.betrag === 'number') {
      einzelpreisNetto = produktDto.berechnungsregel.betrag;
    }

    /*
     * INFO:
     * Die Produktbeschreibung wird beim Anlegen der Position als Initialwert mitgesendet,
     * damit sie in der DB als position.beschreibung landet und beim Festschreiben automatisch
     * im PDF/ZUGFeRD erscheint. Spaetere Aenderungen erfolgen ueber den Update-Pfad und
     * koennen die Beschreibung auch bewusst leeren ('') - hier nicht zurueckfallen.
     */
    return {
      produktId: produktDto.id,
      menge: 1,
      einzelpreisNetto: einzelpreisNetto,
      beschreibung: produktDto.beschreibung ?? undefined,
    };
  }

}
